if (!customElements.get('pd-preview-nav')) {
  customElements.define('pd-preview-nav', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.desktop = window.matchMedia('(min-width: 1070px)');
      this.shell = this.querySelector('.pd-nav__shell');
      this.body = this.querySelector('.pd-nav__body');
      this.toggle = this.querySelector('.pd-nav__toggle');
      this.back = this.querySelector('.pd-nav__back');
      this.details = [...this.querySelectorAll('.pd-nav__details')];
      this.groups = [...this.querySelectorAll('.pd-nav__group')];
      this.applyLayout();
      this.desktop.addEventListener('change', () => this.applyLayout(), options);
      this.shell.addEventListener('toggle', () => this.syncShell(), options);
      this.addEventListener('click', event => {
        if (event.target.closest('.pd-nav__back')) {this.goBack();return;}
        if (event.target.closest('.pd-nav__close')) {this.closeAll(true);return;}
        const link = event.target.closest('a[href]');
        if (link) {
          const url = new URL(link.href, location.href);
          if (url.hash && url.origin === location.origin && url.pathname === location.pathname && url.search === location.search) {
            // Nimbus must not show its page-loading overlay for an in-page jump.
            link.setAttribute('data-no-transition', '');
            if (!event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!link.target || link.target === '_self') && !link.hasAttribute('download')) {
              if (this.desktop.matches) this.closeAll(false);
              else this.closeShell(false);
            }
          }
          return;
        }
        const summary = event.target.closest('summary');
        if (!summary || summary === this.toggle) return;
        const selected = summary.parentElement;
        if (selected.matches('.pd-nav__group')) {
          event.preventDefault();
          if (!this.desktop.matches) {
            const panel = selected.closest('.pd-nav__panel');
            this.parentScroll = panel.scrollTop;
            panel.scrollTop = 0;
            this.groups.forEach(group => {group.open = group === selected;});
            this.syncMobile(true);
          }
          return;
        }
        event.preventDefault();
        this.clearTimers();
        const open = this.hoverOpened === selected || !selected.open;
        this.details.forEach(detail => {detail.open = detail === selected && open;});
        this.hoverOpened = null;
        this.syncMobile(true);
      }, options);
      this.details.forEach(detail => {
        detail.addEventListener('pointerenter', event => {
          if (!this.desktop.matches || event.pointerType !== 'mouse') return;
          this.clearTimers();
          this.openTimer = setTimeout(() => {
            this.details.forEach(other => {other.open = other === detail;});
            this.hoverOpened = detail;
          }, 110);
        }, options);
        detail.addEventListener('pointerleave', () => clearTimeout(this.openTimer), options);
      });
      this.addEventListener('pointerleave', () => {
        if (!this.desktop.matches) return;
        clearTimeout(this.openTimer);
        this.closeTimer = setTimeout(() => this.closeAll(false), 220);
      }, options);
      this.addEventListener('pointerenter', () => clearTimeout(this.closeTimer), options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          if (!this.desktop.matches) {
            if (this.details.some(detail => detail.open)) this.goBack();
            else this.closeShell();
          } else this.closeAll(true);
        }
        if (event.key === 'ArrowDown' && event.target.matches('.pd-nav__trigger') && event.target.tagName === 'SUMMARY') {
          event.preventDefault();
          const selected = event.target.parentElement;
          this.details.forEach(detail => {detail.open = detail === selected;});
          this.syncMobile(true);
          if (this.desktop.matches) selected.querySelector('.pd-nav__panel a')?.focus();
        }
        if (event.key === 'Tab' && !this.desktop.matches && this.shell.open) {
          const focusable = [...this.shell.querySelectorAll('a[href], button, summary, select, input, [tabindex="0"]')].filter(el => el.tabIndex >= 0 && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (event.shiftKey && event.target === first) {event.preventDefault();last?.focus();}
          else if (!event.shiftKey && event.target === last) {event.preventDefault();first?.focus();}
        }
      }, options);
      this.addEventListener('focusout', event => {
        if (this.desktop.matches && event.relatedTarget && !this.contains(event.relatedTarget)) this.closeAll(false);
      }, options);
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) {
          this.closeAll(false);
          if (!this.desktop.matches && this.shell.open) this.closeShell(false);
        }
      }, options);
      window.addEventListener('resize', () => {
        if (!this.desktop.matches && this.shell.open) this.positionDrawer();
      }, options);
    }
    applyLayout() {
      this.closeAll(false);
      this.shell.open = this.desktop.matches;
      this.groups.forEach(group => {
        group.open = this.desktop.matches;
        group.querySelector('summary').tabIndex = this.desktop.matches ? -1 : 0;
      });
      if (this.desktop.matches) this.removeAttribute('data-mobile-depth');
      else this.dataset.mobileDepth = '0';
      this.syncShell();
    }
    positionDrawer() {
      const bottom = this.closest('.header').getBoundingClientRect().bottom;
      this.style.setProperty('--pd-menu-top', Math.max(0, bottom) + 'px');
    }
    syncMobile(focus = false) {
      if (this.desktop.matches) return;
      const detail = this.details.find(item => item.open);
      const group = detail?.querySelector('.pd-nav__group[open]');
      const current = group || detail;
      this.dataset.mobileDepth = group ? '2' : detail ? '1' : '0';
      if (current) {
        const summary = current.querySelector('summary');
        const title = [...summary.childNodes].filter(node => node.nodeType === Node.TEXT_NODE).map(node => node.textContent).join('').trim();
        this.back.querySelector('.pd-nav__back-title').textContent = title;
        this.back.setAttribute('aria-label', this.back.dataset.label + ': ' + title);
        this.body.scrollTop = 0;
        if (focus) {
          current.querySelector(group ? '.pd-nav__group-body' : '.pd-nav__panel').scrollTop = 0;
          this.back.focus({preventScroll:true});
        }
      }
    }
    goBack() {
      const detail = this.details.find(item => item.open);
      const group = detail?.querySelector('.pd-nav__group[open]');
      const current = group || detail;
      if (!current) return;
      current.open = false;
      this.syncMobile();
      if (group) detail.querySelector('.pd-nav__panel').scrollTop = this.parentScroll || 0;
      current.querySelector('summary').focus({preventScroll:true});
    }
    syncShell() {
      const mobileOpen = !this.desktop.matches && this.shell.open;
      document.documentElement.classList.toggle('pd-menu-open', mobileOpen);
      this.closest('.section-header')?.classList.toggle('menu-open', mobileOpen);
      this.toggle.setAttribute('aria-expanded', String(mobileOpen));
      this.toggle.setAttribute('aria-label', mobileOpen ? this.toggle.dataset.labelClose : this.toggle.dataset.labelOpen);
      if (mobileOpen) {
        this.positionDrawer();
        this.body.setAttribute('role', 'dialog');
        this.body.setAttribute('aria-modal', 'true');
        this.querySelector('.pd-nav__trigger').focus({preventScroll:true});
      } else {
        this.body.removeAttribute('role');
        this.body.removeAttribute('aria-modal');
        if (!this.desktop.matches) {
          this.closeAll(false);
          this.groups.forEach(group => {group.open = false;});
          this.body.scrollTop = 0;
        }
      }
    }
    closeShell(restoreFocus = true) {
      this.shell.open = false;
      this.syncShell();
      if (restoreFocus) this.toggle.focus();
    }
    clearTimers() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
    }
    closeAll(restoreFocus) {
      this.clearTimers();
      const current = this.details?.find(detail => detail.open);
      this.details?.forEach(detail => {detail.open = false;});
      this.hoverOpened = null;
      if (this.body) this.syncMobile();
      if (restoreFocus) current?.querySelector('summary').focus();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.clearTimers();
      document.documentElement.classList.remove('pd-menu-open');
      this.closest('.section-header')?.classList.remove('menu-open');
    }
  });
}

